import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getUser, setUser } from '../api';
import './Admin.css';

export default function Settings() {
  const navigate = useNavigate();
  const user = getUser();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Pet editing
  const [pets, setPets] = useState([]);
  const [newPetName, setNewPetName] = useState('');
  const [newPetSpecies, setNewPetSpecies] = useState('');
  const [newPetBirth, setNewPetBirth] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.get('/users/me').then((res) => {
      setProfile(res.data);
      setName(res.data.name);
      setEmail(res.data.email);
      setPets(res.data.pets || []);
    });
  }, []); // eslint-disable-line

  const saveProfile = async () => {
    setMsg(''); setErr('');
    try {
      const data = {};
      if (name !== profile.name) data.name = name;
      if (email !== profile.email) data.email = email;
      if (newPassword) {
        data.old_password = oldPassword;
        data.new_password = newPassword;
      }
      if (Object.keys(data).length === 0) return setMsg('Нет изменений');
      const res = await api.patch('/users/me', data);
      const updated = { ...profile, ...res.data };
      setProfile(updated);
      // Обновляем user в localStorage
      const currentUser = getUser();
      if (currentUser) {
        if (res.data.name) currentUser.name = res.data.name;
        if (res.data.email) currentUser.email = res.data.email;
        setUser(currentUser);
      }
      setOldPassword('');
      setNewPassword('');
      setMsg('Сохранено');
    } catch (e) {
      setErr(e.response?.data?.error?.message || 'Ошибка');
    }
  };

  const savePet = async (pet) => {
    try {
      await api.patch('/users/me/pets/' + pet.id, { name: pet.name, species: pet.species, birth_date: pet.birth_date });
      setMsg('Питомец обновлён');
    } catch (e) {
      setErr(e.response?.data?.error?.message || 'Ошибка');
    }
  };

  const addPet = async () => {
    if (!newPetName.trim()) return setErr('Введите имя питомца');
    try {
      const res = await api.post('/users/me/pets', { name: newPetName, species: newPetSpecies, birth_date: newPetBirth || null });
      setPets([...pets, res.data]);
      setNewPetName('');
      setNewPetSpecies('');
      setNewPetBirth('');
      setMsg('Питомец добавлен');
    } catch (e) {
      setErr(e.response?.data?.error?.message || 'Ошибка');
    }
  };

  const removePet = async (petId) => {
    if (!window.confirm('Удалить питомца?')) return;
    await api.delete('/users/me/pets/' + petId);
    setPets(pets.filter((p) => p.id !== petId));
    setMsg('Питомец удалён');
  };

  const updatePetField = (petId, field, value) => {
    setPets(pets.map((p) => p.id === petId ? { ...p, [field]: value } : p));
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr('Файл больше 5 МБ'); return; }
    setAvatarUploading(true);
    setMsg(''); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/users/${profile.id}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfile({ ...profile, avatar_key: res.data.avatar_key, avatar_url: res.data.avatar_url });
      setMsg('Аватар обновлён');
    } catch (e) {
      setErr(e.response?.data?.error?.message || 'Ошибка загрузки');
    }
    setAvatarUploading(false);
  };

  const removeAvatar = async () => {
    if (!window.confirm('Удалить аватар?')) return;
    setMsg(''); setErr('');
    try {
      await api.delete(`/users/${profile.id}/avatar`);
      setProfile({ ...profile, avatar_key: null, avatar_url: null });
      setMsg('Аватар удалён');
    } catch (e) {
      setErr(e.response?.data?.error?.message || 'Ошибка');
    }
  };

  if (!profile) return null;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Настройки</h1>
        <button className="admin-back" onClick={() => navigate('/')}>&#8592; Назад в чат</button>
      </div>

      {msg && <div className="settings-msg success">{msg}</div>}
      {err && <div className="settings-msg error">{err}</div>}

      <div className="admin-section">
        <h2 className="settings-section-title">Аватар</h2>
        <div className="settings-avatar">
          <div className="settings-avatar__preview">
            {profile.avatar_url
              ? <img src={profile.avatar_url} alt="Аватар" />
              : <div className="settings-avatar__placeholder">{(profile.name || '?').slice(0, 1).toUpperCase()}</div>}
          </div>
          <div className="settings-avatar__actions">
            <label className={'admin-btn primary' + (avatarUploading ? ' disabled' : '')} style={{ cursor: avatarUploading ? 'default' : 'pointer', margin: 0 }}>
              {avatarUploading ? 'Загрузка...' : (profile.avatar_url ? 'Сменить' : 'Загрузить')}
              <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} disabled={avatarUploading} onChange={(e) => { uploadAvatar(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
            {profile.avatar_url && <button className="admin-btn" onClick={removeAvatar}>Удалить</button>}
            <p className="auth-hint" style={{ margin: 0 }}>JPG/PNG/WEBP до 5 МБ. Автоматически обрезается в квадрат 256×256.</p>
          </div>
        </div>
      </div>

      <div className="admin-section" style={{ marginTop: 20 }}>
        <h2 className="settings-section-title">Профиль</h2>
        <div className="settings-form">
          <div className="settings-field">
            <label>ФИО</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="settings-field">
            <label>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>

        <h2 className="settings-section-title">Сменить пароль</h2>
        <div className="settings-form">
          <div className="settings-field">
            <label>Текущий пароль</label>
            <input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} placeholder="Введите текущий пароль" />
          </div>
          <div className="settings-field">
            <label>Новый пароль</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Минимум 6 символов" />
          </div>
        </div>

        <button className="admin-btn primary" onClick={saveProfile} style={{ marginTop: 16 }}>Сохранить</button>
      </div>

      <div className="admin-section" style={{ marginTop: 20 }}>
        <h2 className="settings-section-title">Мои питомцы</h2>

        {pets.map((pet) => (
          <div key={pet.id} className="settings-pet">
            <div className="settings-form">
              <div className="settings-field">
                <label>Кличка</label>
                <input value={pet.name} onChange={(e) => updatePetField(pet.id, 'name', e.target.value)} />
              </div>
              <div className="settings-field">
                <label>Вид</label>
                <input value={pet.species || ''} onChange={(e) => updatePetField(pet.id, 'species', e.target.value)} placeholder="кот, собака..." />
              </div>
              <div className="settings-field">
                <label>Дата рождения</label>
                <input type="date" value={pet.birth_date ? pet.birth_date.substring(0, 10) : ''} onChange={(e) => updatePetField(pet.id, 'birth_date', e.target.value)} />
              </div>
            </div>
            <div className="settings-pet-actions">
              <button className="admin-btn primary" onClick={() => savePet(pet)}>Сохранить</button>
              <button className="admin-btn" onClick={() => removePet(pet.id)}>Удалить</button>
            </div>
          </div>
        ))}

        <div className="settings-pet add">
          <h3>Добавить питомца</h3>
          <div className="settings-form">
            <div className="settings-field">
              <label>Кличка</label>
              <input value={newPetName} onChange={(e) => setNewPetName(e.target.value)} placeholder="Имя питомца" />
            </div>
            <div className="settings-field">
              <label>Вид</label>
              <input value={newPetSpecies} onChange={(e) => setNewPetSpecies(e.target.value)} placeholder="кот, собака..." />
            </div>
            <div className="settings-field">
              <label>Дата рождения</label>
              <input type="date" value={newPetBirth} onChange={(e) => setNewPetBirth(e.target.value)} />
            </div>
          </div>
          <button className="admin-btn primary" onClick={addPet} style={{ marginTop: 12 }}>Добавить</button>
        </div>
      </div>
    </div>
  );
}
